// Dr.Rx™ UID-Scoped Clinical Data Export Modal
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>;

interface DownloadDataModalProps {
    onClose: () => void;
}

const DownloadDataModal: React.FC<DownloadDataModalProps> = ({ onClose }) => {
    const { uid } = useAuth();
    const { showToast } = useToast();
    const [downloaded, setDownloaded] = useState(false);

    const handleDownload = () => {
        const data: Record<string, any> = {
            exportedAt: new Date().toISOString(),
            platform: 'Dr.Rx™ Clinical Co-Pilot',
        };
        const keysToExport = [
            uid ? `chatSessions_${uid}` : 'chatSessions',
            uid ? `chatSettings_${uid}` : 'chatSettings',
            uid ? `userDrugIndex_${uid}` : 'userDrugIndex',
            uid ? `drugFavorites_${uid}` : 'drugFavorites',
            uid ? `drugRecents_${uid}` : 'drugRecents',
            uid ? `drrx_missed_cards_${uid}` : 'drrx_missed_cards',
            uid ? `drrx_bookmarked_cards_${uid}` : 'drrx_bookmarked_cards',
        ];

        keysToExport.forEach(key => {
            const item = localStorage.getItem(key);
            if (item) {
                try {
                    data[key] = JSON.parse(item);
                } catch {
                    data[key] = item;
                }
            }
        });

        const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data, null, 2))}`;
        const link = document.createElement('a');
        link.href = jsonString;
        link.download = `drrx_clinical_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloaded(true);
        showToast('Clinical data archive downloaded.', 'success');
    };

    return (
        <div onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50 page-enter-active">
            <div onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-[#1E2E4A] to-[#15243E] rounded-2xl border border-blue-900/60 w-full max-w-md text-gray-300 p-8 shadow-2xl relative text-center">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white"><CloseIcon /></button>
                <div className="flex justify-center mb-4">
                    <DownloadIcon />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Export Clinical Data</h2>
                
                {!downloaded ? (
                    <>
                        <p className="text-gray-400 text-sm mb-6">
                            Download a portable JSON archive of your consultations, custom drug index entries, favorites, and study progress.
                        </p>
                        <button onClick={handleDownload} className="w-full bg-yellow-400 text-gray-950 font-bold py-3 rounded-xl hover:bg-yellow-300 transition-all shadow-md">
                            Download JSON Archive
                        </button>
                    </>
                ) : (
                    <div className="bg-emerald-900/30 p-6 rounded-xl border border-emerald-700/50">
                        <h3 className="text-xl font-bold text-emerald-300">Export Complete</h3>
                        <p className="text-emerald-200/80 text-sm mt-2">Your archive has been saved to your device.</p>
                        <button onClick={onClose} className="mt-4 bg-yellow-400 text-gray-950 font-bold py-2 px-6 rounded-xl hover:bg-yellow-300">
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DownloadDataModal;